import { t, Trans } from '@lingui/macro';
import classnames from 'classnames';
import React, { FunctionComponent, useState } from 'react';
import { Document, Page, PasswordResponses, pdfjs } from 'react-pdf';
import { OnPasswordCallback } from 'react-pdf/dist/cjs/shared/types';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { BFLoader } from '@components/common/loader/main';
import { TextButton } from '@components/library/button/index';

if (BFG.environment !== 'js-test') {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

interface PDFViewerProps {
  fileUrl: string;
  backupUrl: string;
}

const PDFViewer: FunctionComponent<PDFViewerProps> = ({ fileUrl, backupUrl }) => {
  const [hasError, setHasError] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [passwordCancelled, setPasswordCancelled] = useState(false);

  const nextPage = (): void => {
    if (pageNumber === pageCount) {
      setPageNumber(1);
    } else {
      setPageNumber((prevPageNumber) => prevPageNumber + 1);
    }
  };

  const prevPage = (): void => {
    if (pageNumber === 1) {
      setPageNumber(pageCount);
    } else {
      setPageNumber((prevPageNumber) => prevPageNumber - 1);
    }
  };

  const pageNavigation = (
    <div
      className={classnames(
        'pdf-viewer-nav',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        { 'pdf-viewer-nav__single-page': pageCount === 1 }
      )}
    >
      {pageCount > 1 && (
        <TextButton
          className="pdf-viewer-nav--button"
          icon="bff-arrow-left"
          onClick={prevPage}
        >
          <Trans>Previous</Trans>
        </TextButton>
      )}
      <span className='pdf-viewer-nav--page-count'>
        <Trans>Page {pageNumber} of {pageCount}</Trans>
      </span>
      {pageCount > 1 && (
        <TextButton
          className="pdf-viewer-nav--button"
          onClick={nextPage}
        >
          <>
            <Trans>Next</Trans>
            <span className="bff-arrow-right" />
          </>
        </TextButton>
      )}
    </div>
  );

  if (passwordCancelled) return <p>{t`Can't open PDF without password.`}</p>;

  return (
    <div
      className="pdf-viewer--container"
      data-private
      data-testid="pdf-viewer--container"
    >
      {pageCount > 0 && pageNavigation}
      <Document
        error={t`Failed to load PDF file.`}
        file={hasError ? backupUrl : fileUrl}
        loading={<BFLoader />}
        onError={(): void =>  setHasError(true)}
        onLoadError={(): void => setHasError(true)}
        onLoadSuccess={({ numPages }: { numPages: number }): void => setPageCount(numPages)}
        onPassword={(callback, reason) => {
          const callbackProxy: OnPasswordCallback = (password): void => {
            if (password === null) {
              // user hit cancel button, password is null, do nothing to close the prompt
              // user hitting ok without a password will an empty string, will hit callback below
              setPasswordCancelled(true);
            } else {
              callback(password);
            }
          };

          switch (reason) {
            case PasswordResponses.NEED_PASSWORD: {
              const password = prompt(t`Enter the password to open this PDF file.`);
              callbackProxy(password);
              break;
            }
            case PasswordResponses.INCORRECT_PASSWORD: {
              const password = prompt(t`Invalid password. Please try again.`);
              callbackProxy(password);
              break;
            }
            default:
          }
        }}
      >
        <Page
          key={`page_${pageNumber}`}
          canvasBackground="white"
          error={t`Failed to load PDF file.`}
          loading={<BFLoader />}
          pageNumber={pageNumber}
          renderAnnotationLayer={false}
          renderTextLayer={false}
        />
      </Document>
    </div>
  );
};

export default PDFViewer;
